import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import RemoveRoundedIcon from "@mui/icons-material/RemoveRounded";
import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Inventory2RoundedIcon from "@mui/icons-material/Inventory2Rounded";
import ChecklistRoundedIcon from "@mui/icons-material/ChecklistRounded";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import { productsApi } from "../../../api/products";
import MovementFormDialog from "../../../components/dashboard/erp/MovementFormDialog";
import InitialStockCountDialog from "../../../components/dashboard/erp/InitialStockCountDialog";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle } from "../../../components/dashboard/erp/ErpUi";
import ContextualHelp from "../../../components/dashboard/ContextualHelp";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import {
  ERP_BRAND,
  entityId,
  formatDateTime,
  formatMoney,
  projectProductCode,
  projectProductName,
  projectProductStock,
} from "../../../utils/erp";
import { hasProjectPermission } from "../../../utils/projectAccess";
import { useListScrollRestoration, usePersistentListValue } from "../../../utils/listViewState";

function productUnit(product = {}) {
  return String(product.unit || product.unidad || "").trim();
}

function productReorderPoint(product = {}) {
  const value = Number(product.inventory?.reorderPoint ?? product.reorderPoint ?? 0);
  return Number.isFinite(value) ? value : 0;
}

function stockStatus(product = {}) {
  const stock = projectProductStock(product);
  const reorderPoint = productReorderPoint(product);
  if (stock <= 0) return { label: "Sin stock", color: "error", variant: "filled" };
  if (reorderPoint > 0 && stock <= reorderPoint) return { label: "Reponer", color: "warning", variant: "outlined" };
  return { label: "Disponible", color: "success", variant: "outlined" };
}

export default function ProjectErpStockPage() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { access, refreshBootstrap } = useOutletContext();
  const canManageStock = hasProjectPermission(access, "erp.stock.manage");
  const canConfirmMovements = hasProjectPermission(access, "erp.movements.confirm");
  const canReadItems = hasProjectPermission(access, "items.read") || hasProjectPermission(access, "items.update") || hasProjectPermission(access, "items.create");
  const canManageCount = canManageStock && hasProjectPermission(access, "items.read");
  const [stockMovements, setStockMovements] = React.useState([]);
  const [products, setProducts] = React.useState([]);
  const [productsLoading, setProductsLoading] = React.useState(true);
  const [productSearch, setProductSearch] = React.useState("");
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = usePersistentListValue(`erp-stock:${projectId}:page`, 0);
  const [rowsPerPage, setRowsPerPage] = usePersistentListValue(`erp-stock:${projectId}:page-size`, 25);
  const [filters, setFilters] = usePersistentListValue(`erp-stock:${projectId}:filters`, { productId: "", dateFrom: "", dateTo: "" });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [movementAction, setMovementAction] = React.useState(null);
  const [stockCountOpen, setStockCountOpen] = React.useState(false);
  const [activeStockCount, setActiveStockCount] = React.useState(null);
  const [activeStockCountLoaded, setActiveStockCountLoaded] = React.useState(false);

  const loadProducts = React.useCallback(async () => {
    try {
      setProductsLoading(true);
      const data = await productsApi.list(projectId, { stockTracked: true });
      setProducts(data.products || []);
    } catch (err) {
      setError((current) => current || err.message || "No se pudieron cargar las existencias actuales");
      setProducts([]);
    } finally {
      setProductsLoading(false);
    }
  }, [projectId]);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listStockMovements(projectId, { page: page + 1, pageSize: rowsPerPage, ...filters });
      setStockMovements(data.stockMovements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudo cargar la trazabilidad de inventario");
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  const loadActiveStockCount = React.useCallback(async () => {
    setActiveStockCountLoaded(false);
    if (!canManageCount) {
      setActiveStockCount(null);
      setActiveStockCountLoaded(true);
      return null;
    }
    try {
      const data = await erpApi.getActiveStockCount(projectId);
      const next = data.session ? { session: data.session, summary: data.summary } : null;
      setActiveStockCount(next);
      return next;
    } catch {
      // La consulta de continuación es secundaria: no debe bloquear la pantalla Stock.
      setActiveStockCount(null);
      return null;
    } finally {
      setActiveStockCountLoaded(true);
    }
  }, [canManageCount, projectId]);

  React.useEffect(() => { loadProducts(); }, [loadProducts]);
  React.useEffect(() => { load(); }, [load]);
  React.useEffect(() => { loadActiveStockCount(); }, [loadActiveStockCount]);

  React.useEffect(() => {
    if (!activeStockCountLoaded || searchParams.get("conteo") !== "1") return;
    if (canManageCount) setStockCountOpen(true);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("conteo");
    setSearchParams(nextParams, { replace: true });
  }, [activeStockCountLoaded, canManageCount, searchParams, setSearchParams]);

  React.useEffect(() => {
    if (productsLoading || searchParams.get("inventario") !== "inicial") return;
    if (canManageStock && products.length > 0) {
      setMovementAction({ kind: "opening_balance", productId: "", product: null });
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("inventario");
    setSearchParams(nextParams, { replace: true });
  }, [canManageStock, products.length, productsLoading, searchParams, setSearchParams]);

  useListScrollRestoration(`erp-stock:${projectId}`, !loading && !productsLoading);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const filteredProducts = React.useMemo(() => {
    const term = String(productSearch || "").trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) => [
      projectProductName(product),
      projectProductCode(product),
      product.category,
      product.categoria,
      productUnit(product),
    ].some((value) => String(value || "").toLowerCase().includes(term)));
  }, [productSearch, products]);

  const openMovement = React.useCallback((kind, productId = "", product = null) => {
    if (!canManageStock) return;
    setMovementAction({ kind, productId: String(productId || ""), product });
  }, [canManageStock]);

  const refreshAfterMovement = React.useCallback(async () => {
    await Promise.all([loadProducts(), load(), refreshBootstrap?.()]);
  }, [load, loadProducts, refreshBootstrap]);

  const actionButtons = canManageStock ? (
    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
      {canManageCount ? (
        <Box sx={{ width: { xs: "100%", sm: "auto" } }}>
          <Button
            variant={activeStockCount ? "contained" : "outlined"}
            startIcon={<ChecklistRoundedIcon />}
            onClick={() => setStockCountOpen(true)}
            sx={{ minHeight: 46, width: { xs: "100%", sm: "auto" }, textTransform: "none", fontWeight: 900 }}
          >
            {activeStockCount ? "Continuar conteo" : "Conteo inicial"}
          </Button>
          {activeStockCount?.summary ? (
            <Typography variant="caption" sx={{ display: "block", mt: 0.35, color: ERP_BRAND.muted, textAlign: { xs: "left", sm: "center" } }}>
              {activeStockCount.summary.counted} de {activeStockCount.summary.total} contados · {activeStockCount.summary.pending} pendientes
            </Typography>
          ) : null}
        </Box>
      ) : null}
      <Button
        variant="contained"
        startIcon={<AddRoundedIcon />}
        onClick={() => openMovement("stock_in")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Ingresar stock
      </Button>
      <Button
        variant="outlined"
        startIcon={<RemoveRoundedIcon />}
        onClick={() => openMovement("stock_out")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Descontar stock
      </Button>
      <Button
        variant="outlined"
        startIcon={<TuneRoundedIcon />}
        onClick={() => openMovement("stock_adjustment")}
        sx={{ minHeight: 46, textTransform: "none", fontWeight: 900 }}
      >
        Corregir stock
      </Button>
    </Stack>
  ) : null;

  return (
    <Stack spacing={2.4}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}
      <ErpSectionTitle
        title="Stock"
        description="Consultá existencias primero. Las entradas, salidas y correcciones quedan registradas como movimientos."
        action={actionButtons}
      />

      <ErpPanel>
        <Box sx={{ p: { xs: 1.25, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack spacing={1.25}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
              <Box>
                <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>Existencias actuales</Typography>
                <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>
                  Ítems que controlan stock. Buscá y operá sin abrir el historial.
                </Typography>
              </Box>
              <Tooltip title="Actualizar existencias">
                <span>
                  <IconButton
                    onClick={() => Promise.all([loadProducts(), load()])}
                    disabled={loading || productsLoading}
                    aria-label="Actualizar stock"
                    sx={{ border: `1px solid ${ERP_BRAND.border}`, borderRadius: 2.5 }}
                  >
                    <RefreshRoundedIcon />
                  </IconButton>
                </span>
              </Tooltip>
            </Stack>
            <TextField
              value={productSearch}
              onChange={(event) => setProductSearch(event.target.value)}
              placeholder="Buscar ítem por nombre, código o categoría"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start"><SearchRoundedIcon sx={{ color: ERP_BRAND.muted }} /></InputAdornment>
                ),
              }}
            />
          </Stack>
        </Box>

        {productsLoading ? (
          <ErpLoading label="Cargando existencias actuales..." />
        ) : !products.length ? (
          <ErpEmpty
            title="Todavía no hay ítems con control de stock"
            description="Creá o configurá un producto o insumo que controle existencias para empezar a operar stock."
            action={canReadItems ? (
              <Button
                variant="contained"
                startIcon={<Inventory2RoundedIcon />}
                onClick={() => navigate(`/dashboard/proyectos/${projectId}/productos`)}
                sx={{ textTransform: "none", fontWeight: 900 }}
              >
                Ir a Ítems
              </Button>
            ) : null}
          />
        ) : !filteredProducts.length ? (
          <ErpEmpty
            title="No encontramos ese ítem"
            description="Probá con otro nombre o código."
            action={<Button onClick={() => setProductSearch("")} sx={{ textTransform: "none", fontWeight: 900 }}>Limpiar búsqueda</Button>}
          />
        ) : (
          <TableContainer sx={{ p: { xs: 1.1, sm: 0 } }}>
            <ResponsiveOperationalTable
              mobileLabel="Existencias actuales"
              collapseAfter={4}
              mobileEssentialLabels={["Ítem", "Existencia", "Estado"]}
            >
              <TableHead>
                <TableRow>
                  <TableCell>Ítem</TableCell>
                  <TableCell align="right">Existencia</TableCell>
                  <TableCell>Unidad</TableCell>
                  <TableCell>Estado</TableCell>
                  <TableCell>Código</TableCell>
                  {canManageStock ? <TableCell align="right">Acciones</TableCell> : null}
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredProducts.map((product) => {
                  const id = entityId(product);
                  const status = stockStatus(product);
                  return (
                    <TableRow key={id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 950, color: ERP_BRAND.dark }}>{projectProductName(product)}</Typography>
                        {product.category || product.categoria ? (
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>{product.category || product.categoria}</Typography>
                        ) : null}
                      </TableCell>
                      <TableCell align="right">
                        <Typography sx={{ fontSize: 20, fontWeight: 950, color: ERP_BRAND.dark }}>{projectProductStock(product)}</Typography>
                      </TableCell>
                      <TableCell>{productUnit(product) || "—"}</TableCell>
                      <TableCell><Chip size="small" label={status.label} color={status.color} variant={status.variant} sx={{ fontWeight: 850 }} /></TableCell>
                      <TableCell>{projectProductCode(product) || "—"}</TableCell>
                      {canManageStock ? (
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.75} justifyContent="flex-end" useFlexGap flexWrap="wrap">
                            <Button size="small" variant="contained" onClick={() => openMovement("stock_in", id, product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Ingresar</Button>
                            <Button size="small" variant="outlined" onClick={() => openMovement("stock_out", id, product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Descontar</Button>
                            <Button size="small" variant="text" onClick={() => openMovement("stock_adjustment", id, product)} sx={{ minHeight: 40, textTransform: "none", fontWeight: 850 }}>Corregir</Button>
                          </Stack>
                        </TableCell>
                      ) : null}
                    </TableRow>
                  );
                })}
              </TableBody>
            </ResponsiveOperationalTable>
          </TableContainer>
        )}
      </ErpPanel>

      <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
        <ContextualHelp
          compact
          title="Cómo leer el stock"
          text="Cada cambio conserva el saldo anterior, la variación y el saldo posterior. Ingresar y descontar son movimientos operativos; corregir sirve para diferencias de conteo."
          bullets={["No se edita la existencia directamente.", "El historial queda disponible para auditoría y trazabilidad."]}
        />
      </Box>

      <ErpPanel>
        <Box sx={{ p: { xs: 1.25, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <Stack spacing={1.2}>
            <Box>
              <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950 }}>Historial de stock</Typography>
              <Typography variant="body2" sx={{ color: ERP_BRAND.muted }}>Movimientos confirmados para revisar qué cambió y cuándo.</Typography>
            </Box>
            <ResponsiveListControls
              search={(
                <Box sx={{ display: "flex", alignItems: "center", minHeight: 40, flex: 1 }}>
                  <Typography variant="body2" sx={{ color: ERP_BRAND.muted, fontWeight: 800 }}>
                    {total} {total === 1 ? "movimiento" : "movimientos"}
                  </Typography>
                </Box>
              )}
              activeFilterCount={[filters.productId, filters.dateFrom, filters.dateTo].filter(Boolean).length}
              onClear={() => {
                setFilters({ productId: "", dateFrom: "", dateTo: "" });
                setPage(0);
              }}
              drawerTitle="Filtrar historial de stock"
            >
              <TextField select size="small" label="Producto" value={filters.productId} onChange={(event) => setFilter("productId", event.target.value)} sx={{ minWidth: { sm: 320 } }}>
                <MenuItem value="">Todos los productos</MenuItem>
                {products.map((product) => <MenuItem key={entityId(product)} value={entityId(product)}>{projectProductName(product)}{projectProductCode(product) ? ` · ${projectProductCode(product)}` : ""}</MenuItem>)}
              </TextField>
              <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
              <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
            </ResponsiveListControls>
          </Stack>
        </Box>
        {loading ? <ErpLoading label="Cargando movimientos de inventario..." /> : !stockMovements.length ? <ErpEmpty title="Sin movimientos de inventario" description="Las ventas, compras, entradas, salidas y ajustes confirmados aparecerán aquí." /> : (
          <>
            <TableContainer>
              <ResponsiveOperationalTable mobileLabel="Movimientos de stock" collapseAfter={4}>
                <TableHead>
                  <TableRow>
                    <TableCell>Fecha</TableCell>
                    <TableCell>Producto</TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell align="right">Existencia anterior</TableCell>
                    <TableCell align="right">Variación</TableCell>
                    <TableCell align="right">Existencia posterior</TableCell>
                    <TableCell align="right">Costo unitario</TableCell>
                    <TableCell>Usuario</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {stockMovements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell>{formatDateTime(movement.occurredAt)}</TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontWeight: 900 }}>{movement.productNameSnapshot}</Typography>
                        {movement.metadata?.recipeIngredient === true ? (
                          <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>
                            Receta · {movement.metadata?.recipeParentProductName || "producto"}{movement.metadata?.sourceNumber ? ` · ${movement.metadata.sourceNumber}` : ""}
                          </Typography>
                        ) : null}
                      </TableCell>
                      <TableCell>{movement.kind === "in" ? "Entrada" : movement.kind === "out" ? "Salida" : movement.kind === "adjustment" ? "Ajuste" : "Reversa"}</TableCell>
                      <TableCell align="right">{movement.stockBefore}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 950, color: Number(movement.quantityDelta) >= 0 ? "success.main" : "error.main" }}>{Number(movement.quantityDelta) > 0 ? `+${movement.quantityDelta}` : movement.quantityDelta}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{movement.stockAfter}</TableCell>
                      <TableCell align="right">{formatMoney(movement.unitCost)}</TableCell>
                      <TableCell>{movement.createdByName || "—"}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination component="div" count={total} page={page} onPageChange={(_event, nextPage) => setPage(nextPage)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }} rowsPerPageOptions={[10, 25, 50, 100]} labelRowsPerPage="Filas por página" />
          </>
        )}
      </ErpPanel>

      <MovementFormDialog
        open={Boolean(movementAction)}
        onClose={() => setMovementAction(null)}
        onSaved={refreshAfterMovement}
        projectId={projectId}
        access={access}
        initialKind={movementAction?.kind || ""}
        initialProductId={movementAction?.productId || ""}
        initialProduct={movementAction?.product || null}
      />

      {canManageCount ? (
        <InitialStockCountDialog
          open={stockCountOpen}
          onClose={() => setStockCountOpen(false)}
          projectId={projectId}
          activeCount={activeStockCount}
          canConfirm={canConfirmMovements}
          onChanged={async (event) => {
            await loadActiveStockCount();
            if (event?.completed) {
              await refreshAfterMovement();
            } else {
              await refreshBootstrap?.();
            }
          }}
          onGoItems={() => {
            setStockCountOpen(false);
            navigate(`/dashboard/proyectos/${projectId}/productos`);
          }}
          onGoMovements={() => {
            setStockCountOpen(false);
            navigate(`/dashboard/proyectos/${projectId}/sistema/movimientos`);
          }}
        />
      ) : null}
    </Stack>
  );
}
